// Advanced Leads Management Component with Add/Delete/Checkboxes
import { useState, useEffect, useRef, useCallback } from "react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { 
  Download, Upload, Search, Filter, Plus,
  FileSpreadsheet, Clock, Phone, Mail,
  Globe, Stethoscope, MessageSquare, MoreHorizontal, Trash2
} from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const WA = "917289007800";

function getAuth() {
  const token = localStorage.getItem("admin_token");
  return { headers: { Authorization: `Bearer ${token}` } };
}

const COUNTRIES = [
  "All Countries", "Nigeria", "Ghana", "Kenya", "Tanzania", "Uganda", "Ethiopia", 
  "South Africa", "UAE", "Saudi Arabia", "Oman", "Bangladesh", "Nepal", 
  "Afghanistan", "USA", "UK", "Canada", "Other"
];

const LEAD_TYPES = ["All Types", "consultation", "contact", "career", "hospital_inquiry", "partner_inquiry"];
const STATUS_OPTIONS = ["all", "new", "contacted", "qualified", "converted", "lost"];

export default function LeadsManagement() {
  const [leads, setLeads] = useState([]);
  const [filteredLeads, setFilteredLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filters, setFilters] = useState({
    type: "All Types",
    country: "All Countries",
    status: "all",
    dateFrom: "",
    dateTo: ""
  });
  const [showFilters, setShowFilters] = useState(false);
  const [selectedLeads, setSelectedLeads] = useState([]);
  const [viewLead, setViewLead] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newLead, setNewLead] = useState({
    name: "", email: "", phone: "", country: "", lead_type: "consultation", treatment: "", message: "", status: "new"
  });
  const fileInputRef = useRef(null);

  useEffect(() => { loadLeads(); }, []);

  const loadLeads = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/leads?limit=500`, getAuth());
      setLeads(res.data.leads || []);
    } catch (err) {
      console.error("Failed to load leads:", err);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = useCallback(() => {
    let result = [...leads];
    if (search) {
      const q = search.toLowerCase();
      result = result.filter(l => 
        l.name?.toLowerCase().includes(q) ||
        l.email?.toLowerCase().includes(q) ||
        l.phone?.includes(q) ||
        l.treatment?.toLowerCase().includes(q)
      );
    }
    if (filters.type !== "All Types") result = result.filter(l => l.lead_type === filters.type);
    if (filters.country !== "All Countries") result = result.filter(l => l.country === filters.country);
    if (filters.status !== "all") result = result.filter(l => l.status === filters.status);
    if (filters.dateFrom) result = result.filter(l => new Date(l.created_at) >= new Date(filters.dateFrom));
    if (filters.dateTo) result = result.filter(l => new Date(l.created_at) <= new Date(filters.dateTo));
    setFilteredLeads(result);
  }, [leads, search, filters]);

  useEffect(() => { applyFilters(); }, [applyFilters]);

  const exportToCSV = () => {
    const headers = ["Name", "Email", "Phone", "Country", "Type", "Treatment", "Status", "Date", "Message"];
    const rows = filteredLeads.map(l => [
      l.name, l.email, l.phone, l.country || "", l.lead_type, 
      l.treatment || l.subject || l.position || "", l.status, 
      l.created_at?.split("T")[0], l.message || ""
    ]);
    const csvContent = [headers, ...rows].map(row => row.map(cell => `"${cell}"`).join(",")).join("\n");
    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `paxgrow_leads_${new Date().toISOString().split("T")[0]}.csv`;
    a.click();
  };

  const handleImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const formData = new FormData();
    formData.append("file", file);
    try {
      await axios.post(`${API}/admin/leads/import`, formData, {
        ...getAuth(),
        headers: { ...getAuth().headers, "Content-Type": "multipart/form-data" }
      });
      loadLeads();
      alert("Leads imported successfully!");
    } catch (err) {
      alert("Failed to import leads. Please check the file format.");
    }
  };

  const addLead = async () => {
    if (!newLead.name || !newLead.email) {
      alert("Name and email are required");
      return;
    }
    try {
      await axios.post(`${API}/admin/leads`, newLead, getAuth());
      setShowAddForm(false);
      setNewLead({ name: "", email: "", phone: "", country: "", lead_type: "consultation", treatment: "", message: "", status: "new" });
      loadLeads();
    } catch (err) {
      alert("Failed to add lead");
    }
  };

  const deleteLead = async (leadId) => {
    if (!window.confirm("Delete this lead?")) return;
    try {
      await axios.delete(`${API}/admin/leads/${leadId}`, getAuth());
      loadLeads();
      setSelectedLeads(selectedLeads.filter(id => id !== leadId));
    } catch (err) {
      alert("Failed to delete lead");
    }
  };

  const bulkDelete = async () => {
    if (selectedLeads.length === 0) {
      alert("No leads selected");
      return;
    }
    if (!window.confirm(`Delete ${selectedLeads.length} selected leads?`)) return;
    try {
      await axios.post(`${API}/admin/leads/bulk-delete`, { ids: selectedLeads }, getAuth());
      loadLeads();
      setSelectedLeads([]);
    } catch (err) {
      alert("Failed to delete leads");
    }
  };

  const updateLeadStatus = async (leadId, status) => {
    try {
      await axios.put(`${API}/admin/leads/${leadId}/status`, { status }, getAuth());
      setLeads(leads.map(l => l.id === leadId ? { ...l, status } : l));
    } catch (err) {
      console.error("Failed to update status:", err);
    }
  };

  const toggleSelectAll = () => {
    if (selectedLeads.length === filteredLeads.length) {
      setSelectedLeads([]);
    } else {
      setSelectedLeads(filteredLeads.map(l => l.id));
    }
  };

  const toggleSelectLead = (leadId) => {
    if (selectedLeads.includes(leadId)) {
      setSelectedLeads(selectedLeads.filter(id => id !== leadId));
    } else {
      setSelectedLeads([...selectedLeads, leadId]);
    }
  };

  const formatWA = (lead) => {
    const msg = `*Lead: ${lead.name}*\nEmail: ${lead.email}\nPhone: ${lead.phone}\nType: ${lead.lead_type}\n${lead.treatment ? `Treatment: ${lead.treatment}` : ""}`;
    return `https://wa.me/${WA}?text=${encodeURIComponent(msg)}`;
  };

  const getStatusColor = (status) => {
    const colors = {
      new: "bg-blue-100 text-blue-700",
      contacted: "bg-yellow-100 text-yellow-700",
      qualified: "bg-purple-100 text-purple-700",
      converted: "bg-green-100 text-green-700",
      lost: "bg-red-100 text-red-700"
    };
    return colors[status] || "bg-slate-100 text-slate-700";
  };

  return (
    <div className="space-y-4" data-testid="leads-management">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search leads by name, email, phone..."
            className="pl-10"
            data-testid="lead-search"
          />
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button onClick={() => setShowAddForm(true)} className="bg-orange-600 hover:bg-orange-700" size="sm">
            <Plus className="h-4 w-4 mr-1" /> Add Lead
          </Button>
          {selectedLeads.length > 0 && (
            <Button variant="destructive" size="sm" onClick={bulkDelete}>
              <Trash2 className="h-4 w-4 mr-1" /> Delete ({selectedLeads.length})
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={() => setShowFilters(!showFilters)} className={showFilters ? "bg-orange-50 border-orange-300" : ""}>
            <Filter className="h-4 w-4 mr-1" /> Filters
          </Button>
          <Button variant="outline" size="sm" onClick={exportToCSV}>
            <Download className="h-4 w-4 mr-1" /> CSV
          </Button>
          <Button variant="outline" size="sm" onClick={exportToCSV}>
            <FileSpreadsheet className="h-4 w-4 mr-1" /> Excel
          </Button>
          <input ref={fileInputRef} type="file" accept=".csv,.xlsx" onChange={handleImport} className="hidden" />
          <Button variant="outline" size="sm" onClick={() => fileInputRef.current?.click()}>
            <Upload className="h-4 w-4 mr-1" /> Import
          </Button>
        </div>
      </div>

      {/* Add Lead Form */}
      {showAddForm && (
        <Card className="bg-white rounded-xl border border-slate-100 p-6">
          <h3 className="font-semibold text-slate-900 mb-4">Add New Lead</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div>
              <Label className="text-xs text-slate-500">Name *</Label>
              <Input value={newLead.name} onChange={e => setNewLead({...newLead, name: e.target.value})} placeholder="Full Name" />
            </div>
            <div>
              <Label className="text-xs text-slate-500">Email *</Label>
              <Input type="email" value={newLead.email} onChange={e => setNewLead({...newLead, email: e.target.value})} placeholder="email@example.com" />
            </div>
            <div>
              <Label className="text-xs text-slate-500">Phone</Label>
              <Input value={newLead.phone} onChange={e => setNewLead({...newLead, phone: e.target.value})} placeholder="+1234567890" />
            </div>
            <div>
              <Label className="text-xs text-slate-500">Country</Label>
              <Select value={newLead.country} onValueChange={v => setNewLead({...newLead, country: v})}>
                <SelectTrigger><SelectValue placeholder="Select country" /></SelectTrigger>
                <SelectContent>
                  {COUNTRIES.filter(c => c !== "All Countries").map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-slate-500">Lead Type</Label>
              <Select value={newLead.lead_type} onValueChange={v => setNewLead({...newLead, lead_type: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {LEAD_TYPES.filter(t => t !== "All Types").map(t => <SelectItem key={t} value={t}>{t.replace("_", " ")}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-slate-500">Treatment/Subject</Label>
              <Input value={newLead.treatment} onChange={e => setNewLead({...newLead, treatment: e.target.value})} placeholder="Knee Replacement" />
            </div>
            <div className="md:col-span-2 lg:col-span-3">
              <Label className="text-xs text-slate-500">Message</Label>
              <Textarea value={newLead.message} onChange={e => setNewLead({...newLead, message: e.target.value})} rows={2} />
            </div>
          </div>
          <div className="flex gap-2 mt-4">
            <Button onClick={addLead} className="bg-orange-600 hover:bg-orange-700">Add Lead</Button>
            <Button variant="outline" onClick={() => setShowAddForm(false)}>Cancel</Button>
          </div>
        </Card>
      )}

      {/* Filters Panel */}
      {showFilters && (
        <Card className="p-4 bg-slate-50 border-slate-200">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <div>
              <Label className="text-xs text-slate-500">Lead Type</Label>
              <Select value={filters.type} onValueChange={v => setFilters({...filters, type: v})}>
                <SelectTrigger className="bg-white"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {LEAD_TYPES.map(t => <SelectItem key={t} value={t}>{t === "All Types" ? t : t.replace("_", " ")}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-slate-500">Country</Label>
              <Select value={filters.country} onValueChange={v => setFilters({...filters, country: v})}>
                <SelectTrigger className="bg-white"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {COUNTRIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-slate-500">Status</Label>
              <Select value={filters.status} onValueChange={v => setFilters({...filters, status: v})}>
                <SelectTrigger className="bg-white"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {STATUS_OPTIONS.map(s => <SelectItem key={s} value={s}>{s === "all" ? "All Status" : s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-slate-500">From Date</Label>
              <Input type="date" value={filters.dateFrom} onChange={e => setFilters({...filters, dateFrom: e.target.value})} className="bg-white" />
            </div>
            <div>
              <Label className="text-xs text-slate-500">To Date</Label>
              <Input type="date" value={filters.dateTo} onChange={e => setFilters({...filters, dateTo: e.target.value})} className="bg-white" />
            </div>
          </div>
          <div className="mt-3 flex items-center justify-between">
            <p className="text-xs text-slate-500">{filteredLeads.length} of {leads.length} leads</p>
            <Button variant="ghost" size="sm" onClick={() => setFilters({ type: "All Types", country: "All Countries", status: "all", dateFrom: "", dateTo: "" })}>
              Clear Filters
            </Button>
          </div>
        </Card>
      )}

      {/* Leads Table */}
      <Card className="bg-white rounded-xl border border-slate-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-100">
              <tr>
                <th className="text-left p-3 w-10">
                  <Checkbox
                    checked={filteredLeads.length > 0 && selectedLeads.length === filteredLeads.length}
                    onCheckedChange={toggleSelectAll}
                  />
                </th>
                <th className="text-left p-3 font-medium text-slate-500">Lead</th>
                <th className="text-left p-3 font-medium text-slate-500">Contact</th>
                <th className="text-left p-3 font-medium text-slate-500">Type</th>
                <th className="text-left p-3 font-medium text-slate-500">Details</th>
                <th className="text-left p-3 font-medium text-slate-500">Status</th>
                <th className="text-left p-3 font-medium text-slate-500">Date</th>
                <th className="text-left p-3 font-medium text-slate-500">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredLeads.map((lead, i) => (
                <tr key={lead.id || i} className={`border-b border-slate-50 hover:bg-slate-50 ${selectedLeads.includes(lead.id) ? 'bg-orange-50' : ''}`} data-testid={`lead-row-${i}`}>
                  <td className="p-3">
                    <Checkbox
                      checked={selectedLeads.includes(lead.id)}
                      onCheckedChange={() => toggleSelectLead(lead.id)}
                    />
                  </td>
                  <td className="p-3">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-orange-100 flex items-center justify-center text-orange-600 font-semibold text-xs">
                        {lead.name?.split(" ").map(n => n[0]).join("").slice(0, 2)}
                      </div>
                      <div>
                        <p className="font-medium text-slate-900">{lead.name}</p>
                        {lead.country && <p className="text-xs text-slate-400">{lead.country}</p>}
                      </div>
                    </div>
                  </td>
                  <td className="p-3">
                    <p className="text-slate-600 text-xs">{lead.email}</p>
                    <p className="text-slate-400 text-xs">{lead.phone}</p>
                  </td>
                  <td className="p-3">
                    <Badge variant="outline" className="text-xs capitalize">{lead.lead_type?.replace("_", " ")}</Badge>
                  </td>
                  <td className="p-3 max-w-[200px]">
                    <p className="text-slate-600 text-xs truncate">{lead.treatment || lead.subject || lead.position || lead.hospital_name || "-"}</p>
                  </td>
                  <td className="p-3">
                    <Select value={lead.status || "new"} onValueChange={(v) => updateLeadStatus(lead.id, v)}>
                      <SelectTrigger className={`h-7 text-xs ${getStatusColor(lead.status || "new")}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {STATUS_OPTIONS.filter(s => s !== "all").map(s => (
                          <SelectItem key={s} value={s} className="text-xs capitalize">{s}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </td>
                  <td className="p-3 text-xs text-slate-400">{lead.created_at?.split("T")[0]}</td>
                  <td className="p-3">
                    <div className="flex items-center gap-1">
                      <a href={formatWA(lead)} target="_blank" rel="noopener noreferrer" className="p-1.5 rounded hover:bg-green-50 text-green-600" title="WhatsApp">
                        <MessageSquare className="h-4 w-4" />
                      </a>
                      <button onClick={() => setViewLead(lead)} className="p-1.5 rounded hover:bg-slate-100 text-slate-500" title="View Details">
                        <MoreHorizontal className="h-4 w-4" />
                      </button>
                      <button onClick={() => deleteLead(lead.id)} className="p-1.5 rounded hover:bg-red-50 text-red-500" title="Delete">
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {filteredLeads.length === 0 && (
          <div className="p-8 text-center text-slate-500">No leads found matching your criteria</div>
        )}
      </Card>

      {/* Lead Detail Modal */}
      {viewLead && (
        <Dialog open={!!viewLead} onOpenChange={() => setViewLead(null)}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Lead Details</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-orange-100 flex items-center justify-center text-orange-600 font-semibold">
                  {viewLead.name?.split(" ").map(n => n[0]).join("").slice(0, 2)}
                </div>
                <div>
                  <p className="font-semibold text-slate-900">{viewLead.name}</p>
                  <Badge className={getStatusColor(viewLead.status || "new")}>{viewLead.status || "new"}</Badge>
                </div>
              </div>
              <div className="space-y-2 text-sm">
                <div className="flex items-center gap-2 text-slate-600"><Mail className="h-4 w-4 text-slate-400" />{viewLead.email}</div>
                <div className="flex items-center gap-2 text-slate-600"><Phone className="h-4 w-4 text-slate-400" />{viewLead.phone}</div>
                {viewLead.country && <div className="flex items-center gap-2 text-slate-600"><Globe className="h-4 w-4 text-slate-400" />{viewLead.country}</div>}
                {(viewLead.treatment || viewLead.subject || viewLead.position) && (
                  <div className="flex items-center gap-2 text-slate-600"><Stethoscope className="h-4 w-4 text-slate-400" />{viewLead.treatment || viewLead.subject || viewLead.position}</div>
                )}
                <div className="flex items-center gap-2 text-slate-600"><Clock className="h-4 w-4 text-slate-400" />{viewLead.created_at?.replace("T", " ").split(".")[0]}</div>
              </div>
              {viewLead.message && (
                <div className="p-3 bg-slate-50 rounded-lg">
                  <p className="text-xs text-slate-500 mb-1">Message</p>
                  <p className="text-sm text-slate-700">{viewLead.message}</p>
                </div>
              )}
              <div className="flex gap-2">
                <a href={formatWA(viewLead)} target="_blank" rel="noopener noreferrer" className="flex-1">
                  <Button className="w-full bg-green-600 hover:bg-green-700"><MessageSquare className="h-4 w-4 mr-2" /> WhatsApp</Button>
                </a>
                <a href={`mailto:${viewLead.email}`} className="flex-1">
                  <Button variant="outline" className="w-full"><Mail className="h-4 w-4 mr-2" /> Email</Button>
                </a>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
